import styled from 'styled-components';
import ReactGA from 'react-ga4';
import { Tooltip } from '../../../../components/common/tooltip/Tooltip';
import { useState } from 'react';
import { Icon } from '../../../../components/common/icon/Icon';
import { Button } from '../../../../components/common/buttons/Button';
import { ITractorSuggestedPrice } from '../../../../interfaces/OwnTractor.interfaces';
import { formatPrice } from '../../../../utils/formatter/formatPrice.util';
import { usePatchPokeRatingMutation } from '../../../../hooks/api/user/mutations/usePatchPokeRating';
import { ampli } from '../../../../ampli';
import { postPokeFeedbackRatingToSpreadSheet } from '../../../../api/sheet/postPokeFeedbackRatingToSpreadSheet';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';

export const FEEDBACK_RATING = {
  BAD: 0,
  NORMAL: 5,
  GOOD: 10,
} as const;

export type FeedbackRating = (typeof FEEDBACK_RATING)[keyof typeof FEEDBACK_RATING];

export const FeedbackPopupBody = ({
  ownTractorId,
  model,
  suggestedPrice,
  closeModal,
  refetchMyOwnTractor,
}: {
  ownTractorId: string;
  model: string;
  suggestedPrice: ITractorSuggestedPrice;
  closeModal: () => void;
  refetchMyOwnTractor: () => void;
}) => {
  const [feedbackRating, setFeedbackRating] = useState<FeedbackRating>(
    suggestedPrice.pokeRating ?? FEEDBACK_RATING.NORMAL,
  );

  const { userProfile } = useGetUserProfile();

  const { mutate: patchPokeRating } = usePatchPokeRatingMutation();

  const priceModelDescription = suggestedPrice?.suggestedPrice
    ? `${formatPrice(suggestedPrice.suggestedPrice)}원에 `
    : '';

  const hasExistingFeedback = (rating: unknown): boolean =>
    Object.values(FEEDBACK_RATING).includes(rating as FeedbackRating);

  const handleUpdateFeedbackRating = (rating: FeedbackRating) => {
    setFeedbackRating(rating);
  };

  const submitFeedback = () => {
    patchPokeRating(
      {
        ownTractorId,
        pokeId: suggestedPrice.id,
        reqBody: {
          pokeRating: feedbackRating,
        },
      },
      {
        onSuccess: () => {
          ampli.pvmPokeFeedbackCompleted({
            rating: feedbackRating,
          });
          ReactGA.event({
            category: 'poke',
            action: 'Feedback',
            label: `Rating: ${feedbackRating}`,
            value: feedbackRating,
          });
          postPokeFeedbackRatingToSpreadSheet({
            feebackUserId: userProfile?.id ?? '',
            price: suggestedPrice.suggestedPrice ?? 0,
            pokeRating: feedbackRating,
            pokeId: suggestedPrice.id,
          });
          refetchMyOwnTractor();
          closeModal();
        },
      },
    );
  };

  return (
    <Container>
      <FeedbackDescription>{`내 ${model}트랙터\n${priceModelDescription}팔기에 어떨까?`}</FeedbackDescription>
      <FeedbackIconSection>
        <FeedbackIconContainer>
          <Tooltip
            text={'가격이 낮은 것 같아요'}
            size={'large'}
            position={'top'}
            style={{ left: 'auto', bottom: '110%' }}
            visible={feedbackRating === FEEDBACK_RATING.BAD}
          >
            <FeedbackIconBox
              isSelected={feedbackRating === FEEDBACK_RATING.BAD}
              disabled={hasExistingFeedback(suggestedPrice.pokeRating)}
              onClick={() => handleUpdateFeedbackRating(FEEDBACK_RATING.BAD)}
            >
              <FeedbackIcon isSelected={feedbackRating === FEEDBACK_RATING.BAD}>{'😅'}</FeedbackIcon>
            </FeedbackIconBox>
          </Tooltip>
          <Tooltip
            text={'나쁘지 않은 가격 제안이네요'}
            size={'large'}
            position={'top'}
            style={{ left: 'auto', bottom: '110%' }}
            visible={feedbackRating === FEEDBACK_RATING.NORMAL}
          >
            <FeedbackIconBox
              isSelected={feedbackRating === FEEDBACK_RATING.NORMAL}
              disabled={hasExistingFeedback(suggestedPrice.pokeRating)}
              onClick={() => handleUpdateFeedbackRating(FEEDBACK_RATING.NORMAL)}
            >
              <FeedbackIcon isSelected={feedbackRating === FEEDBACK_RATING.NORMAL}>{'😊'}</FeedbackIcon>
            </FeedbackIconBox>
          </Tooltip>
          <Tooltip
            text={'좋은 가격을 주셨네요 감사해요'}
            size={'large'}
            position={'top'}
            style={{ left: 'auto', bottom: '110%' }}
            visible={feedbackRating === FEEDBACK_RATING.GOOD}
          >
            <FeedbackIconBox
              isSelected={feedbackRating === FEEDBACK_RATING.GOOD}
              disabled={hasExistingFeedback(suggestedPrice.pokeRating)}
              onClick={() => handleUpdateFeedbackRating(FEEDBACK_RATING.GOOD)}
            >
              <FeedbackIcon isSelected={feedbackRating === FEEDBACK_RATING.GOOD}>{'🥰'}</FeedbackIcon>
            </FeedbackIconBox>
          </Tooltip>
        </FeedbackIconContainer>
      </FeedbackIconSection>
      <FeedbackHelperTextBox>
        <Icon name={'OrangeExclamationMark'} size={'Medium'} fill={'#FF9500'} />
        <FeedbackHelperText>{'한번 응답하면 수정할 수 없어요!'}</FeedbackHelperText>
      </FeedbackHelperTextBox>
      <FeedbackButtonContainer>
        <Button
          isDisabled={hasExistingFeedback(suggestedPrice.pokeRating)}
          borderType={'square'}
          color={'filledPrimaryStrong'}
          size={'md'}
          text={{ content: '응답하기', size: 'md' }}
          onClick={submitFeedback}
        />
        <Button
          borderType={'square'}
          color={'filledGreyLight'}
          size={'md'}
          text={{ content: '닫기', size: 'md' }}
          onClick={closeModal}
        />
      </FeedbackButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  width: 100%;

  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const FeedbackDescription = styled.pre`
  padding-bottom: 24px;
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const FeedbackIconSection = styled.div`
  display: flex;
  padding: 30px 0px;
  align-items: flex-start;
  align-self: stretch;
`;

const FeedbackIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const FeedbackIconBox = styled.button<{ isSelected: boolean }>`
  display: flex;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: var(--radius-large, 16px);
  background-color: ${({ isSelected }) =>
    isSelected ? 'var(--Light-Scheme-Monochrome-grey30, #E0E0EB)' : 'var(--Light-Scheme-Monochrome-grey10, #f2f2f7)'};
  transition: 0.2s ease-in-out;
`;

const FeedbackIcon = styled.div<{ isSelected: boolean }>`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  font-family: Pretendard;
  font-size: ${({ isSelected }) => (isSelected ? '32px' : '24px')};
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 31.2px */
`;

const FeedbackHelperTextBox = styled.div`
  display: flex;
  padding-bottom: 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const FeedbackHelperText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const FeedbackButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;
